import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Eye, Download, Mail, Phone, ChevronRight } from "lucide-react";
import { authApi, formatApiError } from "../AuthContext";
import Modal, { ConfirmDialog } from "../Modal";

const TABS = [
  { key: "events", label: "Etkinlik Kayıtları", endpoint: "/admin/event-registrations" },
  { key: "coaches", label: "Antrenör Başvuruları", endpoint: "/admin/coach-applications" },
  { key: "contact", label: "İletişim Mesajları", endpoint: "/admin/contact-messages" },
];

export default function SubmissionsSection() {
  const [tab, setTab] = useState("events");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const current = TABS.find((t) => t.key === tab);

  const load = useCallback(() => {
   setLoading(true)
   authApi.get(current.endpoint)
   .then((r) => setItems(r.data))
   .catch((e) => toast.error(formatApiError(e)))
   .finally(() => setLoading(false))
}, [current.endpoint])

  useEffect(() => { load(); }, [tab, load]);

  const onDelete = async () => {
    await authApi.delete(`${current.endpoint}/${confirmDel.id}`);
    toast.success("Silindi");
    load();
  };

  const exportCsv = () => {
    if (items.length === 0) return;
    const keys = Object.keys(items[0]).filter((k) => k !== "_id");
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [keys.join(","), ...items.map((row) => keys.map((k) => escape(row[k])).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${tab}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-submissions">
      <div className="mb-6">
        <h1 className="heading-lg">Form Gönderimleri</h1>
        <p className="text-sm text-muted-foreground mt-1">Sitedeki formlardan gelen tüm başvuru ve mesajlar.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`subm-tab-${t.key}`}
            className={`rounded-full border px-4 py-2 font-display text-xs font-bold uppercase tracking-wider transition-all ${
              tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border bg-secondary hover:border-primary hover:text-primary"
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto flex items-center gap-3">
          <span className="text-xs font-display uppercase tracking-wider text-muted-foreground">{items.length} kayıt</span>
          <button type="button" onClick={exportCsv} disabled={items.length === 0} className="btn-secondary text-xs px-3 py-2 disabled:opacity-50" data-testid="subm-export-csv">
            <Download className="h-3 w-3" /> CSV
          </button>
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="card-elev p-12 text-center text-muted-foreground" data-testid="subm-empty">Henüz kayıt yok.</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="card-elev p-4 flex items-center gap-3" data-testid={`subm-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-display font-bold uppercase tracking-wider truncate">
                    {it.full_name || it.subject}
                  </span>
                  <span className="text-[10px] font-display uppercase tracking-[0.2em] text-muted-foreground">
                    {(it.created_at || "").slice(0, 10)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {it.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-primary" />{it.phone}</span>}
                  {it.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3 text-primary" />{it.email}</span>}
                  {tab === "events" && <span>{it.branch_or_event}</span>}
                  {tab === "coaches" && <span>{it.expertise} → {it.desired_branch}</span>}
                  {tab === "contact" && <span>{it.subject}</span>}
                </div>
              </div>
              <button type="button" onClick={() => setActive(it)} className="btn-ghost text-xs" data-testid={`subm-view-${it.id}`}>
                <Eye className="h-3 w-3" /> Detay <ChevronRight className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => setConfirmDel(it)} className="btn-ghost text-xs text-destructive hover:text-destructive" data-testid={`subm-delete-${it.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {active && (
        <Modal open onClose={() => setActive(null)} title="Gönderim Detayı" size="lg" testid="subm-detail-modal">
          <div className="space-y-2.5 text-sm">
            {Object.entries(active).filter(([k]) => k !== "_id").map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-2 py-2 border-b border-border">
                <div className="text-[10px] font-display uppercase tracking-[0.2em] text-muted-foreground col-span-1">{k}</div>
                <div className="col-span-2 break-words">{typeof v === "boolean" ? (v ? "✓" : "✗") : String(v ?? "—")}</div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={onDelete} title="Kaydı sil?" body="Bu kayıt kalıcı olarak silinecek." />
    </div>
  );
}
